import { DragEvent, useRef } from "react";
import { ButtonV2 } from "../../../../Atoms";

interface ISpreadsheetDropzoneProps {
  columns: string[];
  onFileSelected: (file: File) => void;
  onDownloadModelo: () => void;
}

/**
 * Real drag-and-drop + click-to-select file input, built for the batch
 * spreadsheet import page only — not promoted to Atoms since nothing else
 * in the app needs this exact tenant-neutral, multi-state visual. See
 * `InputSingleFile` for the older/broken input this deliberately doesn't
 * reuse (dead drag-and-drop copy, broken icon path, non-tenant styling).
 */
export const SpreadsheetDropzone = ({
  columns,
  onFileSelected,
  onDownloadModelo,
}: ISpreadsheetDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) onFileSelected(file);
    event.target.value = "";
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  return (
    <div className="flex flex-col gap-8">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="flex flex-col items-center gap-4 rounded-3xl border border-solid border-[#e5e5ea] bg-white p-8 shadow-[0_8px_24px_0_rgba(0,0,0,0.078)]"
      >
        <div className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] bg-[#e8e8e8]">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 12V3.85L4.4 6.45L3 5L8 0L13 5L11.6 6.45L9 3.85V12H7ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
              fill="#20332F"
            />
          </svg>
        </div>

        <div className="text-center">
          <p className="font-['Mulish'] text-base font-bold text-[#20332f]">
            Arraste a planilha ou selecione o arquivo
          </p>
          <p className="font-['Mulish'] text-[10px] text-[#666]">
            Envie somente arquivos no formato .xls ou .xlsx
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2.5">
          <ButtonV2 type="button" onClick={() => inputRef.current?.click()}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.08329 12.8333C3.76246 12.8333 3.48781 12.7191 3.25933 12.4906C3.03086 12.2621 2.91663 11.9875 2.91663 11.6666V2.33329C2.91663 2.01246 3.03086 1.73781 3.25933 1.50933C3.48781 1.28086 3.76246 1.16663 4.08329 1.16663H7.68538C7.84093 1.16663 7.9892 1.19579 8.13017 1.25413C8.27114 1.31246 8.3951 1.3951 8.50204 1.50204L10.7479 3.74788C10.8548 3.85482 10.9375 3.97878 10.9958 4.11975C11.0541 4.26072 11.0833 4.40899 11.0833 4.56454V11.6666C11.0833 11.9875 10.9691 12.2621 10.7406 12.4906C10.5121 12.7191 10.2375 12.8333 9.91663 12.8333H4.08329ZM6.41663 2.33329H4.08329V11.6666H9.91663V5.83329H8.16663C7.68052 5.83329 7.26732 5.66315 6.92704 5.32288C6.58677 4.9826 6.41663 4.5694 6.41663 4.08329V2.33329ZM7.58329 2.33329V4.08329C7.58329 4.24857 7.6392 4.38711 7.751 4.49892C7.86281 4.61072 8.00135 4.66663 8.16663 4.66663H9.91663V4.56454L7.68538 2.33329H7.58329ZM5.83329 11.0833C5.66802 11.0833 5.52947 11.0274 5.41767 10.9156C5.30586 10.8038 5.24996 10.6652 5.24996 10.5C5.24996 10.3347 5.30586 10.1961 5.41767 10.0843C5.52947 9.97253 5.66802 9.91663 5.83329 9.91663H6.99996C7.16524 9.91663 7.30378 9.97253 7.41558 10.0843C7.52739 10.1961 7.58329 10.3347 7.58329 10.5C7.58329 10.6652 7.52739 10.8038 7.41558 10.9156C7.30378 11.0274 7.16524 11.0833 6.99996 11.0833H5.83329ZM5.83329 8.74996C5.66802 8.74996 5.52947 8.69406 5.41767 8.58225C5.30586 8.47045 5.24996 8.3319 5.24996 8.16663C5.24996 8.00135 5.30586 7.86281 5.41767 7.751C5.52947 7.6392 5.66802 7.58329 5.83329 7.58329H8.16663C8.3319 7.58329 8.47045 7.6392 8.58225 7.751C8.69406 7.86281 8.74996 8.00135 8.74996 8.16663C8.74996 8.3319 8.69406 8.47045 8.58225 8.58225C8.47045 8.69406 8.3319 8.74996 8.16663 8.74996H5.83329Z"
                fill="white"
              />
            </svg>
            Selecionar arquivo
          </ButtonV2>
          <ButtonV2
            type="button"
            variant="outline"
            className="border-[#adb7b5] bg-[#adb7b5]/10 text-[#666]"
            onClick={onDownloadModelo}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.09417 5.88583L7.58333 7.39083V1.75H6.41667V7.39083L4.90583 5.88583L4.08333 6.70833L7 9.625L9.91667 6.70833L9.09417 5.88583ZM11.0833 11.0833V7H12.25V11.0833C12.25 11.725 11.725 12.25 11.0833 12.25H2.91667C2.275 12.25 1.75 11.725 1.75 11.0833V7H2.91667V11.0833H11.0833Z"
                fill="#666666"
              />
            </svg>
            Baixar Modelo
          </ButtonV2>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".xls,.xlsx"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      <div className="rounded-3xl border border-solid border-[#e5e5ea] bg-white p-8 shadow-[0_8px_24px_0_rgba(0,0,0,0.078)]">
        <p className="font-['Mulish'] text-[10px] text-[#666]">
          Colunas Esperadas
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {columns.map((column) => (
            <span
              key={column}
              className="rounded-full border border-solid border-[#adb7b5] bg-[#adb7b5]/10 px-[5px] py-[5px] font-['Inter'] text-[10px] text-[#666]"
            >
              {column}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
